import React from 'react';
import { useAtom } from 'jotai';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';

import { contactMessagesHydratedAtom, contactMessagesListParamsAtom } from '@/store';

export interface ContactMessageSearchFields {
  status: string;
  section_id: string;
  subject: string;
  created_between: string;
}

export function useContactMessagesFilters() {
  const [params, setParams] = useAtom(contactMessagesListParamsAtom);
  const [, setHydrated] = useAtom(contactMessagesHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    sort: parseAsString,
    include: parseAsString,
    'filter[status]': parseAsString,
    'filter[section_id]': parseAsString,
    'filter[subject]': parseAsString,
    'filter[created_between]': parseAsString
  });

  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};
    if (queryParams['filter[status]']) filters.status = queryParams['filter[status]'] || undefined;
    if (queryParams['filter[section_id]']) filters.section_id = queryParams['filter[section_id]'] || undefined;
    if (queryParams['filter[subject]']) filters.subject = queryParams['filter[subject]'] || undefined;
    if (queryParams['filter[created_between]']) filters.created_between = queryParams['filter[created_between]'] || undefined;

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      per_page: queryParams.per_page,
      sort: queryParams.sort || undefined,
      filters,
      include: queryParams.include || prev.include || 'section'
    }));

    setHydrated(true);
  }, [
    queryParams.page,
    queryParams.per_page,
    queryParams.sort,
    queryParams.include,
    queryParams['filter[status]'],
    queryParams['filter[section_id]'],
    queryParams['filter[subject]'],
    queryParams['filter[created_between]'],
    setParams,
    setHydrated
  ]);

  const searchFields: ContactMessageSearchFields = {
    status: params.filters.status || '',
    section_id: params.filters.section_id || '',
    subject: params.filters.subject || '',
    created_between: params.filters.created_between || ''
  };

  const setSearchFields = React.useCallback(
    (updater: (prev: ContactMessageSearchFields) => ContactMessageSearchFields) => {
      const next = updater(searchFields);
      setQueryParams({
        'filter[status]': next.status || null,
        'filter[section_id]': next.section_id || null,
        'filter[subject]': next.subject || null,
        'filter[created_between]': next.created_between || null,
        page: 1
      });
    },
    [searchFields, setQueryParams]
  );

  const applyFilters = React.useCallback(() => {
    setQueryParams({
      'filter[status]': searchFields.status || null,
      'filter[section_id]': searchFields.section_id || null,
      'filter[subject]': searchFields.subject || null,
      'filter[created_between]': searchFields.created_between || null,
      page: 1
    });
  }, [searchFields, setQueryParams]);

  const clearFilters = React.useCallback(() => {
    setQueryParams({
      'filter[status]': null,
      'filter[section_id]': null,
      'filter[subject]': null,
      'filter[created_between]': null,
      page: 1
    });
  }, [setQueryParams]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      setQueryParams({ page });
    },
    [setQueryParams]
  );

  const sortParam = params.sort;
  const setSortParam = React.useCallback(
    (sortValue: string | null) => {
      setQueryParams({ sort: sortValue || null, page: 1 });
    },
    [setQueryParams]
  );

  const includeParam = params.include || 'section';
  const setIncludeParam = React.useCallback(
    (value: string | null) => {
      setQueryParams({ include: value || null });
    },
    [setQueryParams]
  );

  const hasAnyFilter = Boolean(
    queryParams['filter[status]'] ||
      queryParams['filter[section_id]'] ||
      queryParams['filter[subject]'] ||
      queryParams['filter[created_between]']
  );

  return {
    searchFields,
    setSearchFields,
    applyFilters,
    clearFilters,
    handlePageChange,
    hasAnyFilter,
    sortParam,
    setSortParam,
    includeParam,
    setIncludeParam
  };
}

